import {
  IProjectLoanType,
  IYieldCompounding,
} from '~/@api/generated-api-types-project/Api'
import { nonNullable } from '~/@lib/utils/type.utils'
import { formatDate } from '~/@lib/utils/date.utils'
import { formatAmount } from '~/@lib/utils/number.utils'
import { Text } from '~/@mid_ui/typography/Text'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { getProjectInvestmentPeriodString } from '~/@pages_investing/_utils/investmentPeriod.utils'
import { projectTooltipByType } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/projectTooltipByType'
import { ProjectYieldText } from '~/@pages_investing/projects/_projects_ui/project-yield-text/ProjectYieldText'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { formatYieldWithImmunity } from '~/@pages_investing/fee-immunity/feeImmunityDisplay.utils'
import { getProjectLoanTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-loan-type/labelByLoanType'
import { getProjectCollateralSubcategoryLabel } from '~/@pages_investing/projects/_projects_ui/project-collateral/labelByCollateralSubcategory'
import { FeeImmunityBadge } from '~/@pages_investing/fee-immunity/FeeImmunityBadge'
import { DetailHeading } from '../project-detail_ui/detail-heading/DetailHeading'
import {
  DetailItem,
  type DetailItemRow,
} from '../project-detail_ui/detail-item/DetailItem'
import { projectDetailTooltipByType } from '../project-detail_ui/detail-tooltips/detailTooltips'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { transformSecurity } from '~/@lib/utils/riskScoreMapper'
import { ProjectYieldTooltip } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/ProjectYieldTooltip'
import { ProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeLabel'

type Props = { project: ProjectSchema }

export function ProjectV2Parameters({ project }: Props) {
  const isDesktop = useIsDesktop()
  const { hasFeeImmunity } = useFeeImmunity()

  projectTooltipByType.yield = <ProjectYieldTooltip />

  const items = [
    {
      heading: (
        <DetailHeading
          title="ID:"
          tooltip={projectDetailTooltipByType.businessId}
        />
      ),
      value: <Text>{project.businessId}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Typ zaistenia:"
          tooltip={projectTooltipByType.category}
        />
      ),
      value: (
        <Text>
          {getProjectCollateralSubcategoryLabel(
            project.collateralInfo.collateralSubcategory
          )}
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Typ úveru:"
          tooltip={projectDetailTooltipByType.loanType}
        />
      ),
      value: (
        <Text>
          {getProjectLoanTypeLabel(
            project.loanType || IProjectLoanType.AssetBasedLoan
          )}
        </Text>
      ),
    },
    nonNullable(project.riskScore)
      ? {
          heading: (
            <DetailHeading
              title="Skóre zabezpečenia:"
              tooltip={projectTooltipByType.riskScore}
            />
          ),
          value: <Text>{transformSecurity(project.riskScore)}</Text>,
        }
      : null,
    nonNullable(project.creditLine)
      ? {
          heading: (
            <DetailHeading
              title="Celkový úverový rámec:"
              tooltip={projectDetailTooltipByType.creditLine}
            />
          ),
          value: <Text>{formatAmount(project.creditLine)}</Text>,
        }
      : null,
    {
      heading: (
        <DetailHeading
          title="Otvorené k investícii teraz:"
          tooltip={projectTooltipByType.amountMax}
        />
      ),
      value: (
        <Text>
          {formatAmount(
            Math.max(project.amountTarget - project.amountCurrent, 0)
          )}
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title={`Veľkosť tranže ${project.stageNumber}:`}
          tooltip={projectDetailTooltipByType.stageSize}></DetailHeading>
      ),
      value: <Text>{formatAmount(project.amountTarget)}</Text>,
    },
    nonNullable(project.creditLine)
      ? {
          heading: (
            <DetailHeading
              title="Zostáva investovať:"
              tooltip={projectDetailTooltipByType.remainingInvestmentAmount}
            />
          ),
          value: (
            <Text>
              {formatAmount(
                Math.max(project.creditLine - project.sumAllStagesAmount, 0)
              )}
            </Text>
          ),
        }
      : null,
    {
      heading: (
        <DetailHeading
          title="Predpokladaný výnos:"
          tooltip={projectTooltipByType.yield}
        />
      ),
      value: hasFeeImmunity ? (
        <FeeImmunityBadge
          value={formatYieldWithImmunity({
            minYieldValue: project._calculatedValues.minYieldValue,
            maxYieldValue: project._calculatedValues.maxYieldValue,
          })}
          minYieldValue={project._calculatedValues.minYieldValue}
          maxYieldValue={project._calculatedValues.maxYieldValue}
          isCompoundingAnnually={
            project.yieldCompounding === IYieldCompounding.Annually
          }
          showTooltip={false}
        />
      ) : (
        <Text className="flex flex-row gap-px items-center justify-center self-end text-green">
          <ProjectYieldText
            _calculatedValues={project._calculatedValues}
            isYieldCompoundingAnnually={
              project.yieldCompounding === IYieldCompounding.Annually
            }
            yieldIconArrowColor={
              isDesktop
                ? 'var(--color-primary-foreground)'
                : 'var(--color-primary)'
            }
            yieldPercentColor={
              isDesktop ? 'var(--color-green)' : 'var(--color-green-dark)'
            }
            yieldIconSize={22}
            className={isDesktop ? 'text-green' : 'text-green-dark'}
          />
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Typ výnosu:"
          tooltip={projectTooltipByType.paymentType}
        />
      ),
      value: (
        <Text>
          <ProjectPaymentTypeLabel
            projectPaymentType={project.paymentTypeId}
            isYieldCompounding={
              project.yieldCompounding === IYieldCompounding.Annually
            }
          />
        </Text>
      ),
    },
    nonNullable(project.investors)
      ? {
          heading: <DetailHeading title="Počet investorov:" />,
          value: <Text>{project.investors}</Text>,
        }
      : null,
    project.startDate && {
      heading: <DetailHeading title="Čas pridania investície:" />,
      value: <Text>{formatDate(project.startDate)}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Doba úročenia:"
          tooltip={projectTooltipByType.investmentPeriod}
        />
      ),
      value: (
        <Text>
          {getProjectInvestmentPeriodString(
            project.investmentPeriod,
            project.yieldCompounding
          )}{' '}
          m
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Začiatok úročenia:"
          tooltip={projectTooltipByType.revenuesFlowStartDate}
        />
      ),
      value: <Text>{formatDate(project.revenuesFlowStartDate)}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Minimálna investícia:"
          tooltip={projectDetailTooltipByType.minimumInvestment}
        />
      ),
      value: <Text>{formatAmount(project.amountMin)}</Text>,
    },
  ]

  return (
    <DetailItem
      sectionId="investmentParameters"
      title="Parametre investície"
      isParameters={true}
      rows={items.filter(nonNullable) as Array<DetailItemRow>}
    />
  )
}
